import { create } from 'zustand';
import { api, ApiError, mapBackendStatus } from '../api';
import { Ticket, User, Operator, Message, Stats, Template, BackendTicketStatus } from '../types';
import { TicketFilters, defaultFilters } from '../components/FilterModal';

function mergeUsers(existing: User[], incoming: User[]): User[] {
  const byId = new Map(existing.map((user) => [user.id, user]));

  for (const user of incoming) {
    const previous = byId.get(user.id);
    byId.set(user.id, previous ? { ...previous, ...user } : user);
  }

  return Array.from(byId.values());
}

function replaceTicket(tickets: Ticket[], nextTicket: Ticket): Ticket[] {
  const exists = tickets.some((ticket) => ticket.id === nextTicket.id);

  if (!exists) {
    return [nextTicket, ...tickets];
  }

  return tickets.map((ticket) => (ticket.id === nextTicket.id ? { ...ticket, ...nextTicket } : ticket));
}

function computeOperatorsOnline(operators: Operator[]): Operator[] {
  return operators.map((operator) => ({
    ...operator,
    isOnline:
      !!operator.lastLoginAt && Date.now() - new Date(operator.lastLoginAt).getTime() < 10 * 60 * 1000,
  }));
}

function mergeOptimisticMessages(existing: Message[], incoming: Message[]): Message[] {
  const isGenericAttachmentName = (name: string | undefined): boolean => {
    if (!name) return true;
    return ['PHOTO', 'DOCUMENT', 'VIDEO', 'VOICE', 'VIDEO_NOTE', 'AUDIO', 'OTHER', 'Вложение'].includes(
      name.toUpperCase(),
    );
  };

  const optimistic = existing.filter((message) => message.id.startsWith('local_'));
  if (!optimistic.length) {
    return incoming;
  }

  const unmatchedOptimistic = optimistic.filter((localMessage) => {
    const localTimestamp = new Date(localMessage.timestamp).getTime();
    return !incoming.some((serverMessage) => {
      if (serverMessage.senderType !== localMessage.senderType) {
        return false;
      }
      if (Boolean(serverMessage.isInternalNote) !== Boolean(localMessage.isInternalNote)) {
        return false;
      }
      const serverText = (serverMessage.text ?? '').trim();
      const localText = (localMessage.text ?? '').trim();
      const textMatched = serverText === localText;
      const attachmentMatched =
        !!serverMessage.attachment &&
        !!localMessage.attachment &&
        serverMessage.attachment.type === localMessage.attachment.type &&
        (serverMessage.attachment.name === localMessage.attachment.name ||
          isGenericAttachmentName(serverMessage.attachment.name) ||
          isGenericAttachmentName(localMessage.attachment.name));
      if (!textMatched && !attachmentMatched) {
        return false;
      }
      const serverTimestamp = new Date(serverMessage.timestamp).getTime();
      return Math.abs(serverTimestamp - localTimestamp) <= 2 * 60 * 1000;
    });
  });

  return [...incoming, ...unmatchedOptimistic].sort(
    (left, right) => new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime(),
  );
}

interface AppState {
  users: User[];
  tickets: Ticket[];
  operators: Operator[];
  stats: Stats;
  templates: Template[];
  ticketTagCatalog: Array<{ id: string; code: string; label: string }>;
  customerTagCatalog: Array<{ id: string; code: string; label: string }>;
  currentOperatorId: string;
  activeTicketId: string | null;
  activeTab: 'inbox' | 'search' | 'users' | 'stats' | 'settings';
  isMobileListVisible: boolean;
  searchQuery: string;
  isApiOnline: boolean;
  isLoading: boolean;
  loadError: string | null;
  filters: TicketFilters;

  setActiveTab: (tab: AppState['activeTab']) => void;
  setActiveTicketId: (id: string | null) => void;
  setMobileListVisible: (visible: boolean) => void;
  setSearchQuery: (query: string) => void;
  setFilters: (filters: TicketFilters) => void;
  setCurrentOperator: (id: string) => void;
  setLoadError: (error: string | null) => void;

  addMessage: (ticketId: string, message: Message) => void;
  updateTicketStatus: (ticketId: string, status: Ticket['status']) => void;
  assignTicket: (ticketId: string, operatorId: string | null) => void;
  updateTicketPriority: (ticketId: string, priority: Ticket['priority']) => Promise<void>;
  addTagToUser: (userId: string, tag: string) => Promise<void>;
  removeTagFromUser: (userId: string, tag: string) => Promise<void>;
  updateUserNote: (userId: string, note: string) => Promise<void>;
  blockUser: (userId: string) => Promise<void>;
  unblockUser: (userId: string) => Promise<void>;
  addTicketTag: (ticketId: string, tag: string) => Promise<void>;
  removeTicketTag: (ticketId: string, tag: string) => Promise<void>;
  deleteTicket: (ticketId: string) => Promise<void>;
  editMessage: (ticketId: string, messageId: string, text: string) => Promise<void>;
  deleteMessage: (ticketId: string, messageId: string) => Promise<void>;
  deleteMessagesBatch: (ticketId: string, messageIds: string[]) => Promise<void>;
  markTicketAsRead: (ticketId: string) => void;
  addTemplate: (template: Pick<Template, 'title' | 'body'>) => Promise<void>;
  updateTemplate: (id: string, data: Partial<Template>) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;

  bootstrapData: () => Promise<void>;
  resetData: () => void;
  loadTickets: () => Promise<void>;
  loadOperators: () => Promise<void>;
  loadTemplates: () => Promise<void>;
  loadTagCatalogs: () => Promise<void>;
  loadTicketDetails: (ticketId: string) => Promise<void>;
  loadCustomer: (customerId: string) => Promise<void>;
  sendMessageToApi: (ticketId: string, payload: { text?: string; isNote: boolean; file?: File; mediaType?: string }) => Promise<void>;
  updateTicketStatusApi: (ticketId: string, status: BackendTicketStatus) => Promise<void>;
  assignTicketApi: (ticketId: string, operatorId: string | null) => Promise<void>;
  checkApiHealth: () => Promise<void>;
  createOperatorApi: (payload: {
    displayName: string;
    role: Operator['role'];
    telegramId?: string;
    login?: string;
    password?: string;
    isActive?: boolean;
  }) => Promise<Operator>;
  updateOperatorApi: (
    operatorId: string,
    payload: Partial<{
      displayName: string;
      role: Operator['role'];
      telegramId: string | null;
      login: string | null;
      password: string;
      isActive: boolean;
    }>,
  ) => Promise<Operator>;
}

const emptyStats: Stats = {
  totalTickets: 0,
  totalTicketsTrend: 0,
  openTickets: 0,
  avgResponseTimeMin: 0,
  closedPeriod: 0,
  chartByDay: [],
  chartResponseTime: [],
  operatorPerformance: [],
};

export const useStore = create<AppState>((set, get) => ({
  users: [],
  tickets: [],
  operators: [],
  stats: emptyStats,
  templates: api.createTemplates(),
  ticketTagCatalog: [],
  customerTagCatalog: [],
  currentOperatorId: '',
  activeTicketId: null,
  activeTab: 'inbox',
  isMobileListVisible: true,
  searchQuery: '',
  isApiOnline: false,
  isLoading: false,
  loadError: null,
  filters: defaultFilters,

  setActiveTab: (tab) => set({ activeTab: tab, isMobileListVisible: true, activeTicketId: null }),

  setActiveTicketId: (id) =>
    set((state) => {
      if (!id) {
        return { activeTicketId: null, isMobileListVisible: false };
      }

      setTimeout(() => {
        get().markTicketAsRead(id);
        void get().loadTicketDetails(id).catch(() => undefined);
      }, 0);

      return {
        activeTicketId: id,
        isMobileListVisible: false,
        tickets: state.tickets.map((ticket) =>
          ticket.id === id ? { ...ticket, isRead: true, unreadCount: 0 } : ticket,
        ),
      };
    }),

  setMobileListVisible: (visible) => set({ isMobileListVisible: visible }),
  setSearchQuery: (query) => set({ searchQuery: query }),
  setFilters: (filters) => set({ filters }),
  setCurrentOperator: (id) => set({ currentOperatorId: id }),
  setLoadError: (error) => set({ loadError: error }),

  addMessage: (ticketId, message) =>
    set((state) => ({
      tickets: state.tickets.map((ticket) =>
        ticket.id === ticketId
          ? {
              ...ticket,
              messages: [...ticket.messages, message],
              lastMessageAt: message.timestamp,
              isRead: message.senderType === 'operator',
              unreadCount: message.senderType === 'user' ? ticket.unreadCount + 1 : 0,
            }
          : ticket,
      ),
    })),

  updateTicketStatus: (ticketId, status) =>
    set((state) => ({
      tickets: state.tickets.map((ticket) => (ticket.id === ticketId ? { ...ticket, status } : ticket)),
    })),

  assignTicket: (ticketId, operatorId) =>
    set((state) => ({
      tickets: state.tickets.map((ticket) =>
        ticket.id === ticketId ? { ...ticket, assignedTo: operatorId } : ticket,
      ),
    })),

  updateTicketPriority: async (ticketId, priority) => {
    const previous = get().tickets.find((ticket) => ticket.id === ticketId)?.priority;
    set((state) => ({
      tickets: state.tickets.map((ticket) => (ticket.id === ticketId ? { ...ticket, priority } : ticket)),
    }));

    try {
      await api.updateTicketPriority(ticketId, { priority: priority === 'high' ? 'HIGH' : priority === 'low' ? 'LOW' : 'MEDIUM' });
      await get().loadTicketDetails(ticketId);
    } catch (error) {
      if (previous) {
        set((state) => ({
          tickets: state.tickets.map((ticket) => (ticket.id === ticketId ? { ...ticket, priority: previous } : ticket)),
        }));
      }
      throw error;
    }
  },

  addTagToUser: async (userId, tag) => {
    const catalogTag = get().customerTagCatalog.find((item) => item.label === tag || item.code === tag);
    if (!catalogTag) {
      set({ loadError: `Не найден customer tag "${tag}" в backend-каталоге.` });
      return;
    }
    await api.addCustomerTag(userId, catalogTag.id);
    await get().loadCustomer(userId);
  },

  removeTagFromUser: async (userId, tag) => {
    const catalogTag = get().customerTagCatalog.find((item) => item.label === tag || item.code === tag);
    if (!catalogTag) {
      return;
    }
    await api.removeCustomerTag(userId, catalogTag.id);
    await get().loadCustomer(userId);
  },

  updateUserNote: async (userId, note) => {
    const updatedUser = await api.updateCustomerNote(userId, note || null);
    set((state) => ({ users: mergeUsers(state.users, [updatedUser]) }));
  },

  blockUser: async (userId) => {
    const updatedUser = await api.blockCustomer(userId);
    set((state) => ({ users: mergeUsers(state.users, [updatedUser]) }));
  },

  unblockUser: async (userId) => {
    const updatedUser = await api.unblockCustomer(userId);
    set((state) => ({ users: mergeUsers(state.users, [updatedUser]) }));
  },

  addTicketTag: async (ticketId, tag) => {
    const catalogTag = get().ticketTagCatalog.find((item) => item.label === tag || item.code === tag);
    if (!catalogTag) {
      set({ loadError: `Не найден ticket tag "${tag}" в backend-каталоге.` });
      return;
    }
    await api.addTicketTag(ticketId, catalogTag.id);
    await get().loadTicketDetails(ticketId);
  },

  removeTicketTag: async (ticketId, tag) => {
    const catalogTag = get().ticketTagCatalog.find((item) => item.label === tag || item.code === tag);
    if (!catalogTag) {
      return;
    }
    await api.removeTicketTag(ticketId, catalogTag.id);
    await get().loadTicketDetails(ticketId);
  },

  deleteTicket: async (ticketId) => {
    await api.deleteTicket(ticketId);
    set((state) => {
      const nextTickets = state.tickets.filter((ticket) => ticket.id !== ticketId);
      return {
        tickets: nextTickets,
        activeTicketId: state.activeTicketId === ticketId ? null : state.activeTicketId,
        stats: api.buildStats(nextTickets, state.operators),
      };
    });
  },

  editMessage: async (ticketId, messageId, text) => {
    await api.editMessage(ticketId, messageId, text);
    await get().loadTicketDetails(ticketId);
  },

  deleteMessage: async (ticketId, messageId) => {
    await api.deleteMessage(ticketId, messageId);
    await get().loadTicketDetails(ticketId);
  },

  deleteMessagesBatch: async (ticketId, messageIds) => {
    if (!messageIds.length) {
      return;
    }
    await Promise.all(messageIds.map((messageId) => api.deleteMessage(ticketId, messageId)));
    await get().loadTicketDetails(ticketId);
  },

  markTicketAsRead: (ticketId) =>
    set((state) => ({
      tickets: state.tickets.map((ticket) =>
        ticket.id === ticketId ? { ...ticket, isRead: true, unreadCount: 0 } : ticket,
      ),
    })),

  addTemplate: async (template) => {
    const created = await api.createTemplate(template);
    set((state) => ({ templates: [...state.templates, created] }));
  },
  updateTemplate: async (id, data) => {
    const updated = await api.updateTemplate(id, data);
    set((state) => ({
      templates: state.templates.map((template) => (template.id === id ? updated : template)),
    }));
  },
  deleteTemplate: async (id) => {
    await api.deleteTemplate(id);
    set((state) => ({ templates: state.templates.filter((template) => template.id !== id) }));
  },

  checkApiHealth: async () => {
    const isApiOnline = await api.checkHealth();
    set({ isApiOnline });
  },

  resetData: () =>
    set({
      users: [],
      tickets: [],
      operators: [],
      stats: emptyStats,
      templates: [],
      ticketTagCatalog: [],
      customerTagCatalog: [],
      currentOperatorId: '',
      activeTicketId: null,
      loadError: null,
    }),

  bootstrapData: async () => {
    await get().checkApiHealth();

    set({ isLoading: true, loadError: null });

    try {
      await Promise.all([get().loadOperators(), get().loadTickets(), get().loadTemplates(), get().loadTagCatalogs()]);
    } catch (error) {
      set({
        loadError: error instanceof Error ? error.message : 'Не удалось загрузить данные оператора.',
      });
    } finally {
      set({ isLoading: false });
    }
  },

  loadOperators: async () => {
    const operators = computeOperatorsOnline(await api.getOperators());

    set((state) => ({
      operators,
      currentOperatorId: state.currentOperatorId || operators[0]?.id || '',
      stats: api.buildStats(state.tickets, operators),
    }));
  },

  loadTemplates: async () => {
    const templates = await api.getTemplates();
    set({ templates });
  },

  loadTagCatalogs: async () => {
    const [ticketTagCatalog, customerTagCatalog] = await Promise.all([
      api.getTicketTagCatalog(),
      api.getCustomerTagCatalog(),
    ]);
    set({ ticketTagCatalog, customerTagCatalog });
  },

  loadTickets: async () => {
    const { tickets, users } = await api.getTickets({ limit: 100 });

    set((state) => {
      const nextTickets = tickets.map((ticket) => {
        const previous = state.tickets.find((item) => item.id === ticket.id);
        if (!previous) {
          return ticket;
        }

        const isActive = state.activeTicketId === ticket.id;
        return {
          ...ticket,
          messages: previous.messages.length ? previous.messages : ticket.messages,
          isRead: isActive ? true : ticket.isRead,
          unreadCount: isActive ? 0 : ticket.unreadCount,
        };
      });

      return {
        tickets: nextTickets,
        users: mergeUsers(state.users, users),
        stats: api.buildStats(nextTickets, state.operators),
      };
    });
  },

  loadTicketDetails: async (ticketId: string) => {
    try {
      const { ticket, customer } = await api.getTicketDetails(ticketId);

      set((state) => {
        const previousTicket = state.tickets.find((item) => item.id === ticket.id);
        const ticketWithMergedMessages = previousTicket
          ? {
              ...ticket,
              messages: mergeOptimisticMessages(previousTicket.messages, ticket.messages),
            }
          : ticket;
        const nextTickets = replaceTicket(state.tickets, ticketWithMergedMessages);
        const nextUsers = customer ? mergeUsers(state.users, [customer]) : state.users;

        return {
          tickets: nextTickets,
          users: nextUsers,
          stats: api.buildStats(nextTickets, state.operators),
        };
      });

      if (!customer) {
        await get().loadCustomer(ticket.userId);
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return;
      }
      throw error;
    }
  },

  loadCustomer: async (customerId: string) => {
    const customer = await api.getCustomer(customerId, get().tickets);
    set((state) => ({ users: mergeUsers(state.users, [customer]) }));
  },

  sendMessageToApi: async (ticketId: string, payload) => {
    const trimmed = payload.text?.trim() ?? '';
    if (!trimmed && !payload.file) {
      return;
    }

    const optimisticMessageId = `local_${Date.now()}`;
    const targetTicket = get().tickets.find((item) => item.id === ticketId);
    const optimisticMessage: Message = {
      id: optimisticMessageId,
      text: trimmed,
      senderType: 'operator',
      senderId: get().currentOperatorId || undefined,
      ticketId,
      ticketShortId: targetTicket?.shortId,
      timestamp: new Date().toISOString(),
      status: 'sending',
      isInternalNote: payload.isNote,
      attachment: payload.file
        ? {
            type: payload.mediaType === 'PHOTO' ? 'image' : 'file',
            url: '',
            name: payload.file.name,
            size: payload.file.size ? `${Math.round(payload.file.size / 1024)} KB` : undefined,
          }
        : undefined,
    };

    get().addMessage(ticketId, optimisticMessage);

    try {
      if (payload.isNote) {
        await api.createNote(ticketId, { text: trimmed });
      } else {
        await api.sendMessage(ticketId, {
          text: trimmed || undefined,
          file: payload.file,
          mediaType: payload.mediaType,
        });
      }

      set((state) => ({
        tickets: state.tickets.map((ticket) =>
          ticket.id === ticketId
            ? {
                ...ticket,
                messages: ticket.messages.map((message) =>
                  message.id === optimisticMessageId ? { ...message, status: 'sent' } : message,
                ),
              }
            : ticket,
        ),
      }));
      await get().loadTicketDetails(ticketId);
    } catch (error) {
      set((state) => ({
        tickets: state.tickets.map((ticket) =>
          ticket.id === ticketId
            ? {
                ...ticket,
                messages: ticket.messages.filter((message) => message.id !== optimisticMessageId),
              }
            : ticket,
        ),
      }));
      set({ loadError: error instanceof Error ? error.message : 'Не удалось отправить сообщение.' });
      throw error;
    }
  },

  updateTicketStatusApi: async (ticketId: string, status: BackendTicketStatus) => {
    const previous = get().tickets.find((ticket) => ticket.id === ticketId);
    const mappedStatus = mapBackendStatus(status);
    get().updateTicketStatus(ticketId, mappedStatus);
    set((state) => ({
      tickets: state.tickets.map((ticket) =>
        ticket.id === ticketId ? { ...ticket, status: mappedStatus, backendStatus: status } : ticket,
      ),
    }));

    try {
      await api.updateTicketStatus(ticketId, { status });
      await get().loadTicketDetails(ticketId);
    } catch (error) {
      if (previous) {
        set((state) => ({
          tickets: state.tickets.map((ticket) =>
            ticket.id === ticketId
              ? { ...ticket, status: previous.status, backendStatus: previous.backendStatus }
              : ticket,
          ),
        }));
      }
      set({ loadError: error instanceof Error ? error.message : 'Не удалось обновить статус.' });
      throw error;
    }
  },

  assignTicketApi: async (ticketId: string, operatorId: string | null) => {
    const previous = get().tickets.find((ticket) => ticket.id === ticketId)?.assignedTo ?? null;

    get().assignTicket(ticketId, operatorId);

    try {
      const operator = get().operators.find((item) => item.id === operatorId);
      await api.assignTicket(ticketId, {
        operatorId: operatorId ?? null,
        operatorName: operator?.name,
      });
      await get().loadTicketDetails(ticketId);
    } catch (error) {
      get().assignTicket(ticketId, previous);
      set({ loadError: error instanceof Error ? error.message : 'Не удалось назначить тикет.' });
      throw error;
    }
  },

  createOperatorApi: async (payload) => {
    const operator = await api.createOperator(payload);
    set((state) => {
      const operators = computeOperatorsOnline([...state.operators, operator]);
      return {
        operators,
        stats: api.buildStats(state.tickets, operators),
      };
    });
    return operator;
  },

  updateOperatorApi: async (operatorId, payload) => {
    const operator = await api.updateOperator(operatorId, payload);
    set((state) => {
      const operators = computeOperatorsOnline(
        state.operators.map((item) => (item.id === operatorId ? operator : item)),
      );
      return {
        operators,
        currentOperatorId:
          state.currentOperatorId === operatorId ? operator.id : state.currentOperatorId,
        stats: api.buildStats(state.tickets, operators),
      };
    });
    return operator;
  },
}));
